import {Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild, Inject} from '@angular/core';
import {Router} from '@angular/router';
import {PreferencesService} from '../../services/preferences.service';
import {CommunicationService} from '../../services/communication.service';
import {HasSubscriptions} from '../has-subscriptions';
import {GoogleMapsService} from '../../services/google-maps.service';
import {InputComponent} from '../forms/input/input.component';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ListingsService} from '../../services/listings.service';
import {Listing} from '../../classes/listing';
import {UserPreferences} from '../../classes/user-preferences';
import { Pagination } from '../../classes/pagination';
import LatLngLiteral = google.maps.LatLngLiteral;
import { Observable, combineLatest } from 'rxjs';
import { SearchResult as ElasticSearchResult } from '../../classes/object-response';
import { ADD_SAVED_LISTING_PREVIEW_GTM, SITE_SEARCH, SITE_SEARCH_TERM } from '../../classes/analytics-tags';
import { debounceTime, filter, switchMap } from 'rxjs/operators';
import {Helper} from '../../classes/helper';
import {ChangeDetectorRef} from '@angular/core';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { DOCUMENT } from '@angular/platform-browser';
import {NgZone} from '@angular/core';
import {CacheService} from "../../services/cache.service";


declare var google: any;

@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
})
export class SearchBarComponent extends HasSubscriptions implements OnInit, OnChanges {
  @ViewChild(InputComponent) inputComponent: InputComponent;
  @ViewChild('searchBarRef') searchBarRef: ElementRef;
  @Input() showBackButton: boolean = false; // Show the option to go to the previous page (mobile only)
  @Input() showResultsOnInit: boolean = false; // Whether to automatically show results when the component is loaded
  @Input() showAdditionalActionsButtons: boolean = false; // Show the near me and saved searches button
  @Input() shouldShowFiltersAfterSearching: boolean = false; // Whether it should continue to the filters page after searching, only true for mobile
  @Input() isNearMe: boolean = false;
  @Input() canHideSearchResults: boolean = true; // Option as to whether or not we always display the search results
  @Input() focus: boolean = false;
  @Input() rightPositionedIcon = false; // Whether the magnifier glass is right positioned
  @Input() placeholder: string = '';
  @Input() updatePlaceholder: boolean = true;
  @Input() searchIcon: string = '/assets/icons/filters/Search_icon@1x.svg';
  showSearchResults: boolean = false;
  locationResults: SearchResult[] = [];
  mlsMatchResults: SearchResult[] = [];

  recentSearches: SearchResult[] = [];

  locationLatLng: LatLngLiteral;
  isSearching: boolean = false;

  searchForm: FormGroup;

  private _radiusForSearchBias: number = 50000; // in meters
  private _maxLocationResults = 3;
  private _favouriteListings: string[] = [];

  // Use this to keep track of when they've actually made a search request.
  // If they've made one, when they go to click the search bar again, we should clear the input
  private static hasCompletedASearchRequest: boolean = false;
  constructor(
    @Inject(DOCUMENT) private document,
    private _communicationService: CommunicationService,
    private _preferencesService: PreferencesService,
    private _listingService: ListingsService,
    private _googleService: GoogleMapsService,
    private _router: Router,
    private cache: CacheService,
    private _formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    private zone: NgZone
  ) {
    super();
    this.searchForm = this._formBuilder.group({searchInput: ''});
  }

  // Deal with the user clicking outside of the component
  onLoseFocus() {
    setTimeout(() => {
      if (this.canHideSearchResults && !this.searchBarRef.nativeElement.contains(this.document.activeElement)) this.showSearchResults = false;
    })
  }

  ngOnInit() {
    this.addSub(this.searchForm.get('searchInput').valueChanges
      .pipe(debounceTime(200))
      .pipe(filter((searchInput: string) => {
        if (!searchInput || !this.locationLatLng) {
          this._resetResults();
          return false;
        }
        return true;
      }))
      .pipe(switchMap((searchInput: string) => this._getQueryPredictionsSearch(searchInput)))
      .subscribe((s) => this._handleQueryPredictions(s.search, s.listings, s.predictions)));

    this.addSub(this._preferencesService.favouriteListingsChanged.subscribe((s) => this._favouriteListings = s));
    this.addSub(this._preferencesService.recentSearchesChanged.subscribe((recentSearches) => this.recentSearches = recentSearches.map((r) => new SearchResult(r, r, 'recentSearch'))));
    this.addSub(this._preferencesService.positionInfoChanged.subscribe((position) => {
      if (position.centerLatLng) {
        this.locationLatLng = {
          lat: position.centerLatLng.lat,
          lng: position.centerLatLng.lng
        } as LatLngLiteral;
      }
    }));
    this.addSub(this._communicationService.searching.activeSearchText.subscribe((text) => {
      if (this.updatePlaceholder) this.placeholder = text
    }));
  }

  ngOnChanges(changes: SimpleChanges) {
    this.showSearchResults = this.showResultsOnInit;
  }

  onSearchInputClick($event) {
    if (SearchBarComponent.hasCompletedASearchRequest) this.searchInput = '';
    else this._getQueryPredictions();
    this.showSearchResults = true;
  }

  onGoToPreviousScreenClick($event) {
    this._communicationService.navigation.showSearch.next(false);
  }

  onClearInput($event) {
    this.searchInput = '';
    this._resetResults();
    this.inputComponent.inputRef.nativeElement.focus();
  }

  onSubmit() {
    this.focus = false;
    this._searchForLocation();
  }

  /**
   * Select any of the results in the list
   * @param $event
   */
  onSelectResult($event) {
    if (['location', 'recentSearch'].indexOf($event.type) >= 0) {
      this.searchForm.controls.searchInput.setValue($event.text, { emitEvent: false });
      this._searchForLocation();
    }
    else if ($event.type.indexOf('mls') >= 0) {
      this._router.navigate([$event.data.detailUrl]);
      this.showSearchResults = false;
    }

    this.focus = false;
    // Search input field loses the focus when the result is selected
    this._communicationService.searching.activeSearchText.next($event.text);
  }

  onFavouriteClick(listing: Listing) {
    this._preferencesService.toggleFavouriteListing(listing.listingId, ADD_SAVED_LISTING_PREVIEW_GTM);
    listing.getFavouriteIconToUse();
  }

  private _resetResults() {
    this.mlsMatchResults = [];
    this.locationResults = [];
  }

  private _updateMapWithNewPosition(place: google.maps.GeocoderResult) {
    if (!place.geometry) {
      this._resetResults();
     return;
    }
    const position = this._preferencesService.getPosition();
    const bounds = place.geometry.bounds;

    position.zoomLevel = Helper.getZoomLevel(place.types);
    position.centerLatLng = place.geometry.location.toJSON();
    // We wont have bounds for an exact address
    position.zoomLevel = Helper.getZoomLevel(place.types);
    position.centerLatLng = place.geometry.location.toJSON();
    // We wont have bounds for an exact address
    if (bounds) {
      position.north = bounds.getNorthEast().lat();
      position.east = bounds.getNorthEast().lng();
      position.south = bounds.getSouthWest().lat();
      position.west = bounds.getSouthWest().lng();

      this._preferencesService.setPosition(position.centerLatLng.lat, position.centerLatLng.lng,
        position.zoomLevel, bounds.getSouthWest().lat(), bounds.getSouthWest().lng(), bounds.getNorthEast().lat(), bounds.getNorthEast().lng()
      );
    }
    else {
      this._preferencesService.setPosition(position.centerLatLng.lat, position.centerLatLng.lng, position.zoomLevel);
    }

    // console.log('update map with new position');
    // this._preferencesService.saveUserPosition(position);

    this._communicationService.searching.shouldCenterAtUserLocation.next(false);
    this._communicationService.searching.updateMap.next({ location: place.geometry.location.toJSON(), types: place.types, isSearchForLocation: true });
 }

  /**
   * Makes a call to the google API to find the lat,lng coordinates based on a string input
   * Updates the map with the new position if one is found
  */
  private _searchForLocation() {
    if (this.searchInput) {
      // TODO: have to fix zoom level
      this._preferencesService.addRecentSearch(this.searchInput);
      this._googleService.performGeocode(this.searchInput).then(results => {
        const isQuebec = results.some((r) => r.address_components && r.address_components.some((c) => 'quebec' === c.long_name.toLowerCase()));

        if (isQuebec) {
          this.shouldShowFiltersAfterSearching = false;
          this._communicationService.navigation.showSearch.next(false);
          this._communicationService.notifications.showQuebecModal.next(true);
          results = null;
        }

        if (results) {
          // Just use the first result if many are given
          this._updateMapWithNewPosition(results[0]);
          this.cache.remove(this.cache.KeySelectedListingId);
        }
        this.showSearchResults = false;

        if (this.updatePlaceholder) {
          this.placeholder = this.searchInput;
        }
        this._communicationService.searching.activeSearchText.next(this.searchInput);
        this.searchInput = ''; // Reset the search input since we'ved already went to look for the location

        SearchBarComponent.hasCompletedASearchRequest = true;
        if (this.shouldShowFiltersAfterSearching) this._communicationService.toolbar.filtersRequested.next();
        else if (!isQuebec) {
          this.zone.run(() => {
            // If they're on desktop, ensure we always move to the map after they select a result
            this._router.navigate(['/find-real-estate']);
          });
        }
      });
    }
  }

  private _getQueryPredictions() {
    if (!!this.searchInput && !!this.locationLatLng) {
      this._getQueryPredictionsSearch(this.searchInput).toPromise().then((r) => this._handleQueryPredictions(r.search, r.listings, r.predictions));
    }
    else this._resetResults();
  }

  private _getQueryPredictionsSearch(searchInput: string): Observable<{ search: string, listings: ElasticSearchResult<Listing[]>, predictions: google.maps.places.AutocompletePrediction[]}> {
    this.isSearching = true;

    this.analyticsService.eventTrack(SITE_SEARCH, { [SITE_SEARCH_TERM]: searchInput });

    return combineLatest(
      this._listingService.searchListings(searchInput, new Pagination(0, 10)),
      this._googleService.getPlacePredictions(searchInput, new google.maps.LatLng(this.locationLatLng.lat, this.locationLatLng.lng), this._radiusForSearchBias),
      (listings, predictions) => ({ search: searchInput, listings: listings, predictions: predictions})
    );
  }

  /**
   * Makes a call to the following to get results:
   * 1: Google Search API
   * 2: Listing Services to find Listings by MLS ID
  */
  private _handleQueryPredictions(searchInput: string, listings: ElasticSearchResult<Listing[]>, predictionList: google.maps.places.AutocompletePrediction[]) {
    if (listings !== null && listings.results !== null && listings.results.length >= 0) {
      this.mlsMatchResults = listings.results
        .filter((l) => l.mlsNum && l.mlsNum.toLowerCase() === searchInput.toLowerCase())
        .map((l) => {
          l.isFavourite = UserPreferences.containsFavouriteListings(this._favouriteListings, [l.listingId]);
          return new SearchResult(l.mlsNum, l, 'mls');
        });
      }

    if (predictionList && predictionList.length > 0) {
      let list = predictionList.map((e) => new SearchResult(e.description, e.description, 'location'));
        // Find matching Recent Results
      const matchingRecentResults = this.recentSearches.filter(r => r.text.includes(searchInput));
      // Don't include duplicates
      list = list.filter((l) => !matchingRecentResults.includes(l));
      // Combine recent + results
      list = matchingRecentResults.concat(list.splice(0, Math.min(list.length, this._maxLocationResults - matchingRecentResults.length)));
      this.locationResults = list;
    }
    else this.locationResults = [];

    this.isSearching = false;
    this.cdRef.detectChanges();
  }

  get searchInput(): string {
    return this.searchForm.controls.searchInput.value;
  }

  set searchInput(value: string) {
    this.searchForm.controls.searchInput.setValue(value);
  }
}

export class SearchResult {
  constructor(public text: string, public data: any, public type: string) {
    this.text = text;
    this.data = data;
    this.type = type;
    this.id = '' + Date.now() + Math.floor(Math.random() * 1000);
  }

  id: string;
}
